import React, { memo, useContext, useMemo, useState } from "react";
import {
  Icon as MuiIcon,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import NotAvailable from "./NotAvailable";
import StatusChip from "./muiCustom/chip/StatusChip";
import { getFormatDate } from "../helpers/utils";
import {
  LOCATION_STATUS_CONST,
  stripeSubscriptionStatus,
  SUBSCRIPTION_CONST,
} from "../config/constants";
import RpVirtualListBase from "./virtual/RpVirtualList";
// import useUserLocation from "../helpers/hooks/useUserLocation";
import { GlobalContext } from "../helpers/contexts/contexts";
import RpSvgIcon from "./icons/RpSvgIcon";
import { TemporaryClosedStatusNote } from "./muiCustom/note/InlineStatusNote";

type ProfileBrand = {
  brand_name?: string;
  id?: number | string;
};

type ProfileBrandLocation = {
  current_package?: string;
  current_subscription_status?: number | string;
  fk_brand_id?: number | string;
  id?: number | string;
  isDelayed?: boolean;
  is_available_for_renewal?: boolean;
  location_fees_covered?: boolean;
  location_name?: string;
  location_status?: number | string;
  radius?: number | string | null;
  subscription_end_date?: string;
  subscription_start_date?: string;
  upcoming_end_date?: string;
  upcoming_package?: string;
  upcoming_start_date?: string;
};

type ProfileBrandLocationsProps = {
  brand?: ProfileBrand | null;
};

type SubscriptionDetails = {
  endDate?: string;
  heading?: string;
  packageName?: string;
  startDate?: string;
};

const Icon = MuiIcon as React.ComponentType<Record<string, unknown>>;
const RpVirtualList = RpVirtualListBase as React.ComponentType<{
  items?: ProfileBrandLocation[];
  renderItem: (
    _location: ProfileBrandLocation | undefined,
    _index: number
  ) => React.ReactNode;
  style?: React.CSSProperties;
  useWindowScroll?: boolean;
}>;

const ProfileBrandLocations = ({ brand }: ProfileBrandLocationsProps) => {
  const { loggedInUser } = useContext(GlobalContext);

  const [expanded, setExpanded] = useState<string | false>("0");

  const locations = useMemo(() => {
    if (brand) {
      const partLocationData =
        (loggedInUser?.partLocationData as
          | ProfileBrandLocation[]
          | undefined) || [];
      const brandLocations = partLocationData.filter(
        (location) => location.fk_brand_id === brand?.id
      );
      return brandLocations;
    }
    return [];
  }, [brand, loggedInUser]);

  // const {
  //   locations,
  //   loading: isLoadingLocations,
  //   loadMore,
  // } = useUserLocation({ brandId: brand?.id, defaultRows: 10 });

  const handleOnClickAccordion =
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const renderSubscription = ({
    heading = "Current Subscription",
    packageName = "N/A",
    startDate = "N/A",
    endDate = "N/A",
  }: SubscriptionDetails) => {
    return (
      <div className="package-detail">
        <h3 className="heading">{heading}</h3>
        <div className="row p-0 d-flex align-item-center">
          <div className="col p-1 ml-2">
            <h4 className="title pl-1">Plan</h4>
            <p className="value fw-b pl-1">{packageName}</p>
          </div>
          <div className="col p-1">
            <h4 className="title">Start Date</h4>
            <p className="value fw-b">{startDate}</p>
          </div>
          <div className="col p-1">
            <h4 className="title">End Date</h4>
            <p className="value fw-b">{endDate}</p>
          </div>
        </div>
      </div>
    );
  };

  const renderSubscriptionStatus = (location: ProfileBrandLocation = {}) => {
    const {
      current_subscription_status,
      isDelayed,
      is_available_for_renewal,
      location_fees_covered,
    } = location;

    const isPastDue =
      current_subscription_status === stripeSubscriptionStatus.PAST_DUE;

    const isSubscribed =
      current_subscription_status && Number(current_subscription_status)
        ? true
        : false;

    const isActive =
      (location_fees_covered || isSubscribed) &&
      Number(location?.location_status) === LOCATION_STATUS_CONST.OPEN.ID;

    let status = (
      <>
        <StatusChip
          label={
            isPastDue
              ? SUBSCRIPTION_CONST.STATUS.PAST_DUE.TEXT
              : isActive
                ? SUBSCRIPTION_CONST.STATUS.ACTIVE.TEXT
                : SUBSCRIPTION_CONST.STATUS.INACTIVE.TEXT
          }
          status={
            isPastDue
              ? SUBSCRIPTION_CONST.STATUS.PAST_DUE.TYPE
              : isActive
                ? SUBSCRIPTION_CONST.STATUS.ACTIVE.TYPE
                : SUBSCRIPTION_CONST.STATUS.INACTIVE.TYPE
          }
          className="location-status p-2 mb-1"
        />
        <StatusChip
          label={`Media Plan: ${isSubscribed ? SUBSCRIPTION_CONST.STATUS.SUBSCRIBED.TEXT : SUBSCRIPTION_CONST.STATUS.UNSUBSCRIBED.TEXT}`}
          status={
            isSubscribed
              ? SUBSCRIPTION_CONST.STATUS.SUBSCRIBED.TYPE
              : SUBSCRIPTION_CONST.STATUS.UNSUBSCRIBED.TYPE
          }
          className="location-status p-2 mb-1"
        />
        {isDelayed ? (
          <StatusChip
            label={SUBSCRIPTION_CONST.STATUS.EXPIRED.TEXT}
            status={SUBSCRIPTION_CONST.STATUS.EXPIRED.TYPE}
            className="location-status p-2 mb-1"
          />
        ) : null}
        {current_subscription_status &&
        Number(current_subscription_status) &&
        is_available_for_renewal ? (
          <StatusChip
            label={SUBSCRIPTION_CONST.STATUS.UPCOMING_RENEWAL.TEXT}
            status={SUBSCRIPTION_CONST.STATUS.UPCOMING_RENEWAL.TYPE}
            className="location-status p-2 mb-1"
          />
        ) : null}
        <TemporaryClosedStatusNote
          location={location}
          noteSx={{
            minHeight: 15,
            py: "0px",
            mb: "4px",
          }}
        />
      </>
    );

    return (
      <div className="ml-2 d-flex align-items-center mb-2 mb-md-0 flex-wrap">
        {status}
      </div>
    );
  };
  const renderLocations = () => {
    return (
      <RpVirtualList
        style={{
          height: 410,
          overflowX: "hidden",
        }}
        useWindowScroll={false}
        items={locations}
        // endReached={loadMore}
        renderItem={(location, i) => (
          <Accordion
            expanded={expanded === `${i}`}
            onChange={handleOnClickAccordion(`${i}`)}
            key={location.id}
            className="location"
            // TransitionProps={{ unmountOnExit: true, mountOnEnter: true }}
          >
            <AccordionSummary
              className="profile-brand-location-accordion"
              expandIcon={
                <ExpandMoreIcon sx={{ color: "var(--neutral-700)" }} />
              }
            >
              <div className="py-1 w-100">
                <div className="d-flex flex-column flex-md-row justify-content-between">
                  <div>
                    <div className="location-name-box mb-1">
                      <div className="icon">
                        <Icon
                          component={RpSvgIcon}
                          name="location_mark"
                          width="16"
                          height="16"
                          color="primary"
                          className="mr-2"
                        />
                      </div>
                      <span className="location-name mt-1">
                        {location.location_name}
                      </span>
                    </div>
                    {renderSubscriptionStatus(location)}
                  </div>
                  {location &&
                    Number(location?.radius) > 0 &&
                    location?.radius != null && (
                      <Typography
                        variant="caption"
                        component="p"
                        className="fw-b mt-1 ml-2 default-target-radius mr-2"
                      >
                        Default geotargeting: {location?.radius || "N/A"} Miles
                      </Typography>
                    )}
                </div>
              </div>
            </AccordionSummary>
            <AccordionDetails>
              <div className="p-1 row location-subscription-detail ">
                <div className="col-12 col-md-6 mb-2 mb-md-0 border-bottom location-subscription-border">
                  {renderSubscription({
                    heading: "Current Plan",
                    packageName: location?.current_package || "N/A",
                    startDate: location?.subscription_start_date
                      ? getFormatDate(location?.subscription_start_date)
                      : "N/A",
                    endDate: location?.subscription_end_date
                      ? getFormatDate(location?.subscription_end_date)
                      : "N/A",
                  })}
                </div>

                <div className="col-md-6">
                  {renderSubscription({
                    heading: "Upcoming Plan",
                    packageName: location?.upcoming_package || "N/A",
                    startDate: location?.upcoming_start_date
                      ? getFormatDate(location?.upcoming_start_date)
                      : "N/A",
                    endDate: location?.upcoming_end_date
                      ? getFormatDate(location?.upcoming_end_date)
                      : "N/A",
                  })}
                </div>
              </div>
            </AccordionDetails>
          </Accordion>
        )}
      />
    );
  };

  return (
    <div className="card brand-locations profile-card-border">
      <div className="card-body">
        <div className="row text-start mb-2">
          <div className="col-12 mb-2">
            <div>
              <Typography variant="h6" component="h2">
                Locations
              </Typography>
              <Typography
                variant="caption"
                component="p"
                className="brand-name mt-2"
              >
                List of your <strong>{brand?.brand_name || ""}</strong>{" "}
                locations
              </Typography>
            </div>
          </div>
        </div>

        {locations && locations.length ? (
          <div className="locations br-6 ">{renderLocations()}</div>
        ) : (
          // <div className="my-3">{!isLoadingLocations && <NotAvailable />}</div>
          <div className="my-3">
            <NotAvailable />
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(ProfileBrandLocations);
